// src/pages/dashboards/admin/AdminOverview.jsx
// Live stats from GET /admin/stats. Compact KPI strip + charts.

import React, { useEffect, useState } from "react";
import { getStats } from "../../../api/admin";
import { Card, Loading, Input } from "../../../components/ui";
import { DonutChart, BarChart } from "../../../components/charts";
import { Users, Building2, Scan, BarChart3 } from "../../../components/icons";
import { titleCase } from "../../../lib/format";

// brand palette for chart segments
const C = { green: "#FF6B4A", bright: "#FF8C42", blue: "#3B82F6", amber: "#F5C542", muted: "#948A88" };
const ROLE_COLOR = { user: C.green, practitioner: C.blue, company: C.amber, admin: C.muted };
const TIER_COLOR = { free: C.muted, starter: C.blue, pro: C.green, company_starter: C.amber, company_pro: C.bright };

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    getStats(params).then(setStats).catch((e) => setErr(e.message));
  }, [from, to]);

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!stats) return <Loading label="Loading stats" />;

  const roleMap = Object.fromEntries((stats.users_by_role || []).map((r) => [r.role, r.count]));
  const roleData = ["user", "practitioner", "company", "admin"].map((r) => ({
    label: titleCase(r), value: roleMap[r] || 0, color: ROLE_COLOR[r],
  }));
  const tierData = (stats.active_by_tier || []).map((t) => ({
    label: titleCase(t.tier), value: t.count, color: TIER_COLOR[t.tier] || C.muted,
  }));

  const p = stats.period;
  const periodData = p ? [
    { label: "New users", value: p.new_users, color: C.green },
    { label: "Scans", value: p.scans, color: C.bright },
    { label: "Receipts", value: p.new_receipts, color: C.blue },
    { label: "Claims", value: p.new_claims, color: C.amber },
  ] : [];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold">Overview</h1>
        <div className="flex items-end gap-2">
          <label className="block">
            <span className="block text-xs text-tb-muted mb-1">From</span>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" />
          </label>
          <label className="block">
            <span className="block text-xs text-tb-muted mb-1">To</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" />
          </label>
        </div>
      </div>

      {/* compact KPI strip - one card, hairline separated */}
      <Card className="mb-5 overflow-hidden">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-tb-border dark:bg-tb-d-border">
          <Kpi icon={Users} label="Total users" value={stats.users} sub={`${stats.disabled_users ?? 0} disabled`} />
          <Kpi icon={Building2} label="Companies" value={stats.companies}
               sub={stats.active_companies != null ? `${stats.active_companies} active` : "—"} />
          <Kpi icon={Scan} label="Scans today" value={stats.scans_today} />
          <Kpi icon={BarChart3} label="Scans this month" value={stats.scans_month} accent />
        </div>
      </Card>

      {/* distributions as donuts */}
      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <Card className="p-5">
          <p className="text-sm font-semibold mb-4">Users by role</p>
          <DonutChart data={roleData} />
        </Card>
        <Card className="p-5">
          <p className="text-sm font-semibold mb-4">Active subscriptions by tier</p>
          {tierData.length ? <DonutChart data={tierData} />
            : <p className="text-sm text-tb-muted">No active subscriptions.</p>}
        </Card>
      </div>

      {/* period activity as bar chart */}
      {p && (
        <Card className="p-5">
          <p className="text-sm font-semibold mb-4">
            Activity in selected period
            <span className="font-normal text-tb-muted"> · {fmt(p.from)} – {fmt(p.to)}</span>
          </p>
          <BarChart data={periodData} />
        </Card>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="surface p-5 flex flex-col items-center text-center">
      <span className={`grid place-items-center h-10 w-10 rounded-xl mb-3 ${accent ? "bg-tb-brand text-white" : "bg-tb-brand/10 text-tb-brand"}`}>
        <Icon size={18} />
      </span>
      <p className="text-xs uppercase tracking-wider text-tb-muted">{label}</p>
      <p className={`mt-1 text-3xl font-bold ${accent ? "text-tb-brand" : ""}`}>{value ?? "—"}</p>
      <p className="mt-1 text-xs text-tb-muted h-4">{sub || ""}</p>
    </div>
  );
}

function fmt(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}